import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {color, fade, mix, spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";

interface CameraButtonProps {
    disabled?: boolean;
    onClick: () => void;
}

// Pulsating animation keyframes using fade for transparency
const pulseKeyframes = {
    "0%": {
        transform: "scale(1)",
        boxShadow: `0 0 0 0 ${fade(color.red, 0.32)}`,
    },
    "50%": {
        transform: "scale(1.08)",
        boxShadow: `0 0 0 8px ${fade(color.red, 0.08)}`,
    },
    "100%": {
        transform: "scale(1)",
        boxShadow: `0 0 0 0 ${fade(color.red, 0.32)}`,
    },
};

/**
 * An animated button that supports a user capturing a still frame from a camera.
 */
export const CameraButton = ({disabled, onClick}: CameraButtonProps) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    return (
        <Clickable
            disabled={disabled}
            aria-label={t`Capture image from camera`}
            onClick={onClick}
        >
            {({hovered, pressed}) => (
                <View
                    testId="camera-button-icon"
                    style={[
                        styles.button,
                        hovered && styles.hovered,
                        pressed && styles.pressed,
                        disabled && styles.disabled,
                    ]}
                    className={css(
                        !disabled && !prefersReducedMotion && styles.pulsate,
                    )}
                />
            )}
        </Clickable>
    );
};

const styles = StyleSheet.create({
    button: {
        width: 28,
        height: 28,
        padding: spacing.xSmall_8,
        borderRadius: "50%",
        background: color.red,
        border: `2px solid ${mix(color.red, color.white)}`,
        boxShadow: `0 2px 8px 0 ${fade(color.red, 0.16)}`,
        transition:
            "background 0.15s, box-shadow 0.15s, border 0.15s, transform 0.1s",
        outline: "none",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        userSelect: "none",
    },
    hovered: {
        background: mix(color.red, color.white),
        border: `2px solid ${color.red}`,
        boxShadow: `0 4px 16px 0 ${fade(color.red, 0.32)}`,
    },
    pressed: {
        background: fade(color.red, 0.4),
        border: `2px solid ${color.red}`,
        boxShadow: `0 2px 4px 0 ${fade(color.red, 0.08)}`,
        transform: "scale(0.96)",
    },
    disabled: {
        background: color.offBlack16,
        border: `2px solid ${color.offBlack16}`,
        boxShadow: "none",
        cursor: "not-allowed",
        opacity: 0.5,
    },
    pulsate: {
        animationName: pulseKeyframes as any,
        animationDuration: "1.5s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },
});
